import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n/I18nContext";
import AffiliateBanner from "@/components/AffiliateBanner";
import Header from "@/components/Header";
import VatCalculator from "@/components/VatCalculator";
import VatNumberValidator from "@/components/VatNumberValidator";
import AffiliateCard from "@/components/AffiliateCard";
import OssTracker from "@/components/OssTracker";
import QuarterlyEstimator from "@/components/QuarterlyEstimator";
import HowItWorks from "@/components/HowItWorks";
import VatRatesTable from "@/components/VatRatesTable";
import Faq from "@/components/Faq";
import Newsletter from "@/components/Newsletter";
import Footer from "@/components/Footer";
import PopularRoutes from "@/components/PopularRoutes";
import SeoHead from "@/components/SeoHead";
import RouteAnalytics from "@/components/RouteAnalytics";
import BadgePage from "@/components/BadgePage";
import { parsePairSlug, getApplicableRate } from "@/data/vatData";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PageMeta() {
    const { pair: pairParam } = useParams();
    const location = useLocation();
    const pair = parsePairSlug(pairParam);

    if (pair) {
        const rate = pair.to.standard;
        const title = `VAT from ${pair.from.name} to ${pair.to.name} — ${rate}% ${pair.to.name} VAT 2026 | VATcalc.eu`;
        const description = `Calculate VAT for cross-border sales from ${pair.from.name} (${pair.from.standard}%) to ${pair.to.name} (${rate}% standard). Free EU VAT calculator with OSS threshold tracker, VIES validator and 2026 rates.`;
        const ogImage = `${BACKEND}/api/og?seller=${pair.from.code}&buyer=${pair.to.code}&net=100`;
        return (
            <SeoHead
                title={title}
                description={description}
                canonicalPath={location.pathname}
                ogImage={ogImage}
            />
        );
    }
    return (
        <SeoHead
            title="EU VAT Calculator 2026 — Free Cross-Border Tax Tool | VATcalc.eu"
            description="Free EU VAT calculator for 27 countries with 2026 rates. Cross-border VAT, OSS threshold tracker, quarterly estimator and VIES VAT-number validator."
            canonicalPath="/"
            ogImage={`${BACKEND}/api/og?seller=LV&buyer=DE&net=100`}
        />
    );
}

function Home() {
    return (
        <div id="top" className="min-h-screen bg-white text-slate-900">
            <RouteAnalytics />
            <PageMeta />
            <AffiliateBanner />
            <Header />
            <main>
                <VatCalculator />
                <VatNumberValidator />
                <AffiliateCard />
                <OssTracker />
                <QuarterlyEstimator />
                <HowItWorks />
                <PopularRoutes />
                <VatRatesTable />
                <Faq />
                <Newsletter />
            </main>
            <Footer />
        </div>
    );
}

function App() {
    return (
        <div className="App">
            <I18nProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/vat/:pair" element={<Home />} />
                        <Route path="/badge" element={<BadgePage />} />
                    </Routes>
                </BrowserRouter>
                <Toaster richColors position="top-center" />
            </I18nProvider>
        </div>
    );
}

export default App;
